import React, { useState } from "react";
import Decimal from "decimal.js";
import {
  IAccountAffiliate,
  IAffiliateDashboard,
  DashboardPresentationContext,
  IAffiliateRates,
} from "./types";
import { portalTime, isFullFillWaiver } from "./fees";
import {
  CountTag,
  DashboardSection,
  DataTable,
  ExpandedCell,
  Explanation,
  ExplanationGrid,
  FeePanel,
  FeePanels,
  HistoryBox,
  IconButton,
  MobileCard,
  MobileCards,
  MobileStats,
  PointStatus,
  SectionHeading,
  SmallStat,
  Stats,
  SubsectionHeading,
  WelcomeStrip,
  TradersSearch,
} from "./HandoffPointsTraders.style";
import { hasCompleteBudgetHistory } from "./budgetHistory";

type Wallet = IAffiliateDashboard["wallets"][number];
type Point = NonNullable<IAffiliateDashboard["points"]>[number];

function decimal(value: string | undefined, digits = 2): string {
  if (value === undefined || value === "") return "—";
  try {
    const fixed = new Decimal(value).toFixed(digits);
    const [integer, fraction] = fixed.split(".");
    return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${
      fraction ? `.${fraction}` : ""
    }`;
  } catch {
    return "—";
  }
}

function money(value: string | undefined): string {
  const formatted = decimal(value);
  return formatted === "—" ? formatted : `$${formatted}`;
}

function pointAmount(value: string | undefined): string {
  if (value === undefined || value === "") return "—";
  try {
    const fixed = new Decimal(value).toFixed();
    const [integer, fraction] = fixed.split(".");
    return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${
      fraction ? `.${fraction}` : ""
    }`;
  } catch {
    return "—";
  }
}

function pointsFromUsd(value: string | undefined): string {
  if (!value) return "—";
  try {
    return pointAmount(new Decimal(value).mul(4).toString());
  } catch {
    return "—";
  }
}

function sum(values: (string | undefined)[]): string {
  return values
    .reduce<Decimal>((total, value) => total.plus(value || 0), new Decimal(0))
    .toString();
}

function utcDate(value?: string): string {
  if (!value || value.startsWith("0001-")) return "—";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][date.getUTCMonth()];
  return `${day} ${month} ${String(date.getUTCFullYear()).slice(-2)}`;
}

function shortAddress(value: string): string {
  return value.length > 16 ? `${value.slice(0, 6)}…${value.slice(-4)}` : value;
}

function walletName(api: DashboardPresentationContext, wallet: Wallet): string {
  return (
    wallet.nickname?.trim() ||
    api.accountLabel?.(wallet.wallet) ||
    shortAddress(wallet.wallet)
  );
}

function WalletIdentity({
  api,
  wallet,
}: {
  api: DashboardPresentationContext;
  wallet: Wallet;
}) {
  const label = walletName(api, wallet);
  return (
    <>
      <span>{label}</span>
      {label !== wallet.wallet && (
        <small className="wallet-address">{shortAddress(wallet.wallet)}</small>
      )}
    </>
  );
}

function FeeDetails({ wallet }: { wallet: Wallet }) {
  const products: {
    title: string;
    fields: {
      label: string;
      key: keyof IAffiliateRates;
      view: keyof IAccountAffiliate;
    }[];
  }[] = [
    {
      title: "Perps fees",
      fields: [
        { label: "Taker", key: "perp_taker_bps", view: "perp_taker" },
        { label: "Maker", key: "perp_maker_bps", view: "perp_maker" },
      ],
    },
    {
      title: "Options fees",
      fields: [
        { label: "Taker", key: "option_taker_bps", view: "option_taker" },
        { label: "Maker", key: "option_maker_bps", view: "option_maker" },
      ],
    },
  ];
  return (
    <FeePanels>
      {products.map((product) => (
        <FeePanel key={product.title}>
          <h3>{product.title}</h3>
          <table>
            <thead>
              <tr>
                <th>Side</th>
                <th>Current</th>
                <th>Maximum</th>
                <th>Next scheduled</th>
              </tr>
            </thead>
            <tbody>
              {product.fields.map((field) => {
                const fee = wallet[
                  field.view
                ] as IAccountAffiliate["perp_taker"];
                const current =
                  wallet.applied_rates?.[field.key] ??
                  fee?.rate_bps ??
                  wallet.rates?.[field.key];
                return (
                  <tr key={field.key}>
                    <td>{field.label}</td>
                    <td className="current">
                      {current === undefined ? "—" : `${current} bps`}
                    </td>
                    <td>
                      {fee?.ceiling_bps === undefined
                        ? "—"
                        : `${fee.ceiling_bps} bps`}
                    </td>
                    <td
                      className={
                        fee?.next_rate_bps === undefined ? "" : "scheduled"
                      }
                    >
                      {fee?.next_rate_bps === undefined
                        ? "—"
                        : `${fee.next_rate_bps} bps · ${utcDate(
                            fee.next_effective_at
                          )}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </FeePanel>
      ))}
    </FeePanels>
  );
}

function totalVolume(data: IAffiliateDashboard, wallet: string): string {
  const recorded = data.history_totals?.wallets.find(
    (entry) => entry.wallet.toLowerCase() === wallet.toLowerCase()
  )?.notional;
  if (recorded !== undefined) return money(recorded);
  if (data.fills_next_cursor) return "—";
  const fills = data.fills.filter(
    (fill) => fill.request.wallet.toLowerCase() === wallet.toLowerCase()
  );
  if (fills.some((fill) => !fill.accrual.metadata?.input?.notional)) return "—";
  return money(
    sum(fills.map((fill) => fill.accrual.metadata?.input?.notional))
  );
}

function referredVolume(data: IAffiliateDashboard): string {
  if (data.history_totals?.notional !== undefined)
    return money(data.history_totals.notional);
  if (data.fills_next_cursor) return "—";
  if (data.fills.some((fill) => !fill.accrual.metadata?.input?.notional))
    return "—";
  return money(
    sum(data.fills.map((fill) => fill.accrual.metadata?.input?.notional))
  );
}

function recordedBudget(
  data: IAffiliateDashboard,
  wallet: string
): { allocated: string; unspent: string; end: string } {
  if (!hasCompleteBudgetHistory(data))
    return { allocated: "—", unspent: "—", end: "—" };
  const allowances = data.allowances.filter(
    (entry) => entry.wallet.toLowerCase() === wallet.toLowerCase()
  );
  if (!allowances.length) return { allocated: "—", unspent: "—", end: "—" };
  const ends = Array.from(
    new Set(allowances.map((entry) => entry.end_at || ""))
  );
  let end = "Until spent";
  if (ends.length > 1) end = "Multiple dates";
  else if (ends[0]) end = utcDate(ends[0]);
  return {
    allocated: pointsFromUsd(
      sum(allowances.map((entry) => entry.original_usd))
    ),
    unspent: pointsFromUsd(sum(allowances.map((entry) => entry.remaining_usd))),
    end,
  };
}

export function ReferredTradersView({
  data,
  api,
}: {
  data: IAffiliateDashboard;
  api: DashboardPresentationContext;
}) {
  const [openTrader, setOpenTrader] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const traders = data.wallets || [];
  const query = search.trim().toLowerCase();
  const matches = traders.filter((trader) =>
    [
      trader.nickname || "",
      api.accountLabel?.(trader.wallet) || "",
      trader.wallet,
      trader.code || "",
    ].some((value) => value.toLowerCase().includes(query))
  );
  const canEdit = false;

  return (
    <DashboardSection aria-label="Referred traders">
      <SectionHeading>
        <div>
          <h2>Referred traders</h2>
          <CountTag>{traders.length} linked</CountTag>
        </div>
        <SmallStat>
          <small>Referred volume</small>
          <strong>{referredVolume(data)}</strong>
        </SmallStat>
      </SectionHeading>
      {traders.length > 0 && (
        <TradersSearch>
          Search referred traders
          <input
            type="search"
            value={search}
            placeholder="Search nickname, wallet or code"
            onChange={(event) => setSearch(event.target.value)}
          />
        </TradersSearch>
      )}
      {!hasCompleteBudgetHistory(data) && (
        <p>
          Older ended budgets are not included in these totals. Recorded budget
          totals are unavailable.
        </p>
      )}
      {traders.length === 0 ? (
        <p>Share a referral link to start inviting traders.</p>
      ) : (
        <>
          {!matches.length && <p>No traders match your search.</p>}
          <DataTable>
            <thead>
              <tr>
                <th>Trader</th>
                <th>Code</th>
                <th>Linked</th>
                <th>Volume 7d</th>
                <th>Volume 30d</th>
                <th>Total volume</th>
                <th>Zero-fee budget</th>
                <th title="USDC rebates you earned from this trader's fees">
                  Your rebates
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((trader) => {
                const open = openTrader === trader.wallet;
                const budget = recordedBudget(data, trader.wallet);
                return (
                  <React.Fragment key={trader.wallet}>
                    <tr
                      className="expandable"
                      onClick={() => setOpenTrader(open ? null : trader.wallet)}
                    >
                      <td className="primary address" title={trader.wallet}>
                        <WalletIdentity api={api} wallet={trader} />
                      </td>
                      <td>{trader.code || "—"}</td>
                      <td>{utcDate(trader.attached_at)}</td>
                      <td>—</td>
                      <td>—</td>
                      <td className="primary">
                        {totalVolume(data, trader.wallet)}
                      </td>
                      <td>
                        {budget.unspent === "—" ? "—" : `${budget.unspent} pts`}
                      </td>
                      <td className="primary" title="Rebate total unavailable">
                        —
                      </td>
                      <td>
                        <IconButton
                          type="button"
                          disabled={!canEdit}
                          title="Read-only: edit trader fees from the affiliate wallet"
                        >
                          Edit trader
                        </IconButton>{" "}
                        <IconButton
                          type="button"
                          aria-label={`${
                            open ? "Hide" : "Show"
                          } fees for ${walletName(api, trader)}`}
                          aria-expanded={open}
                          onClick={(
                            event: React.MouseEvent<HTMLButtonElement>
                          ) => {
                            event.stopPropagation();
                            setOpenTrader(open ? null : trader.wallet);
                          }}
                        >
                          {open ? "⌃" : "⌄"}
                        </IconButton>
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <ExpandedCell colSpan={9}>
                          <FeeDetails wallet={trader} />
                        </ExpandedCell>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </DataTable>
          <MobileCards>
            {matches.map((trader) => {
              const open = openTrader === trader.wallet;
              const budget = recordedBudget(data, trader.wallet);
              return (
                <MobileCard key={trader.wallet}>
                  <header>
                    <div>
                      <strong title={trader.wallet}>
                        <WalletIdentity api={api} wallet={trader} />
                      </strong>
                      <small>
                        {trader.code || "—"} · Linked{" "}
                        {utcDate(trader.attached_at)}
                      </small>
                    </div>
                    <SmallStat>
                      <small>Your rebates</small>
                      <strong>—</strong>
                    </SmallStat>
                  </header>
                  <MobileStats>
                    <div>
                      <span>Volume 7d</span>
                      <strong>—</strong>
                    </div>
                    <div>
                      <span>Volume 30d</span>
                      <strong>—</strong>
                    </div>
                    <div>
                      <span>Total volume</span>
                      <strong>{totalVolume(data, trader.wallet)}</strong>
                    </div>
                  </MobileStats>
                  <p>
                    Zero-fee budget ·{" "}
                    {budget.unspent === "—"
                      ? "—"
                      : `${budget.unspent} pts unspent`}
                  </p>
                  <div className="actions">
                    <IconButton
                      type="button"
                      disabled={!canEdit}
                      title="Read-only: use the affiliate wallet to edit trader fees"
                    >
                      Edit trader
                    </IconButton>
                    <IconButton
                      type="button"
                      aria-expanded={open}
                      onClick={() => setOpenTrader(open ? null : trader.wallet)}
                    >
                      Fees {open ? "⌃" : "⌄"}
                    </IconButton>
                  </div>
                  {open && <FeeDetails wallet={trader} />}
                </MobileCard>
              );
            })}
          </MobileCards>
        </>
      )}
    </DashboardSection>
  );
}

function pointStatus(
  point: Point,
  data: IAffiliateDashboard,
  now?: string
): string {
  const at = portalTime(now);
  if (new Decimal(point.remaining).isZero()) return "Spent";
  if (Date.parse(point.expires_at) <= at) return "Expired";
  if (data.affiliate?.terminated_at) return "Frozen";
  if (Date.parse(point.available_at) > at) return "Pending";
  return "Available";
}

function Status({ status }: { status: string }) {
  return <PointStatus $status={status}>{status}</PointStatus>;
}

function PointsHistory({
  data,
  api,
}: {
  data: IAffiliateDashboard;
  api: DashboardPresentationContext;
}) {
  const points = (data.points || []).filter(
    (point) => point.kind !== "welcome"
  );
  const ordered = [...points].sort(
    (a, b) => Date.parse(b.available_at) - Date.parse(a.available_at)
  );
  return (
    <>
      <SubsectionHeading>
        <h3>Earned points</h3>
        <small>Latest first · {ordered.length} entries</small>
      </SubsectionHeading>
      {ordered.length === 0 ? (
        <p>No earned points yet.</p>
      ) : (
        <HistoryBox>
          <DataTable>
            <thead>
              <tr>
                <th>Earned</th>
                <th>Points</th>
                <th>Unspent</th>
                <th>Available</th>
                <th>Expires</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((point) => (
                <tr key={point.id}>
                  <td>—</td>
                  <td>{pointAmount(point.original)}</td>
                  <td className="primary">{pointAmount(point.remaining)}</td>
                  <td>{utcDate(point.available_at)}</td>
                  <td>{utcDate(point.expires_at)}</td>
                  <td>
                    <Status status={pointStatus(point, data, api.now)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
          <MobileCards>
            {ordered.map((point) => (
              <MobileCard key={`mobile:${point.id}`}>
                <header>
                  <div>
                    <strong>{pointAmount(point.remaining)} pts unspent</strong>
                    <small>{pointAmount(point.original)} pts earned</small>
                  </div>
                  <Status status={pointStatus(point, data, api.now)} />
                </header>
                <MobileStats>
                  <div>
                    <span>Earned</span>
                    <strong>—</strong>
                  </div>
                  <div>
                    <span>Available</span>
                    <strong>{utcDate(point.available_at)}</strong>
                  </div>
                  <div>
                    <span>Expires</span>
                    <strong>{utcDate(point.expires_at)}</strong>
                  </div>
                </MobileStats>
              </MobileCard>
            ))}
          </MobileCards>
        </HistoryBox>
      )}
    </>
  );
}

function WelcomeGrant({
  data,
  api,
}: {
  data: IAffiliateDashboard;
  api: DashboardPresentationContext;
}) {
  const welcome = (data.points || []).filter(
    (point) => point.kind === "welcome"
  );
  if (welcome.length === 0) return null;
  return (
    <div role="region" aria-label="Welcome grant">
      <SubsectionHeading>
        <h3>Welcome grant</h3>
      </SubsectionHeading>
      {welcome.map((point) => (
        <WelcomeStrip key={point.id}>
          <div>
            <small>Unspent</small>
            <strong>
              {pointAmount(point.remaining)}{" "}
              <span>/ {pointAmount(point.original)} pts</span>
            </strong>
          </div>
          <div>
            <small>Available</small>
            <span>{utcDate(point.available_at)}</span>
          </div>
          <div>
            <small>Expires</small>
            <span>{utcDate(point.expires_at)}</span>
          </div>
          <Status status={pointStatus(point, data, api.now)} />
        </WelcomeStrip>
      ))}
    </div>
  );
}

function WalletBudgetTable({
  data,
  api,
}: {
  data: IAffiliateDashboard;
  api: DashboardPresentationContext;
}) {
  const wallets = data.wallets || [];
  const canFund = false;
  if (wallets.length === 0)
    return <p>No linked wallets yet. Share a code first.</p>;
  return (
    <>
      {!hasCompleteBudgetHistory(data) && (
        <p>
          Older ended budgets are not included in these totals. Recorded budget
          totals are unavailable.
        </p>
      )}
      <DataTable>
        <thead>
          <tr>
            <th>Trader</th>
            <th>Allocated points</th>
            <th>Unspent points</th>
            <th>End date</th>
            <th>Volume 7d</th>
            <th>Volume 30d</th>
            <th>Total volume</th>
            <th title="USDC rebates you earned from this trader's fees">
              Your rebates
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {wallets.map((wallet) => {
            const budget = recordedBudget(data, wallet.wallet);
            return (
              <tr key={wallet.wallet}>
                <td className="primary address" title={wallet.wallet}>
                  <WalletIdentity api={api} wallet={wallet} />
                </td>
                <td>{budget.allocated}</td>
                <td className="primary">{budget.unspent}</td>
                <td>{budget.end}</td>
                <td>—</td>
                <td>—</td>
                <td>{totalVolume(data, wallet.wallet)}</td>
                <td>—</td>
                <td>
                  <IconButton
                    $highlight
                    type="button"
                    disabled={
                      !canFund ||
                      !wallet.attached ||
                      Boolean(wallet.terminated_at)
                    }
                    title="Read-only: use the affiliate wallet to fund a budget"
                  >
                    Fund
                  </IconButton>
                </td>
              </tr>
            );
          })}
        </tbody>
      </DataTable>
      <MobileCards>
        {wallets.map((wallet) => {
          const budget = recordedBudget(data, wallet.wallet);
          return (
            <MobileCard key={wallet.wallet}>
              <header>
                <div>
                  <strong title={wallet.wallet}>
                    <WalletIdentity api={api} wallet={wallet} />
                  </strong>
                  <small>{wallet.code || "Linked trader"}</small>
                </div>
                <IconButton
                  $highlight
                  type="button"
                  disabled={
                    !canFund ||
                    !wallet.attached ||
                    Boolean(wallet.terminated_at)
                  }
                  title="Read-only: use the affiliate wallet to fund a budget"
                >
                  Fund
                </IconButton>
              </header>
              <MobileStats>
                <div>
                  <span>Allocated</span>
                  <strong>{budget.allocated}</strong>
                </div>
                <div>
                  <span>Unspent</span>
                  <strong>{budget.unspent}</strong>
                </div>
                <div>
                  <span>End date</span>
                  <strong>{budget.end}</strong>
                </div>
              </MobileStats>
              <MobileStats>
                <div>
                  <span>Volume 7d</span>
                  <strong>—</strong>
                </div>
                <div>
                  <span>Volume 30d</span>
                  <strong>—</strong>
                </div>
                <div>
                  <span>Total volume</span>
                  <strong>{totalVolume(data, wallet.wallet)}</strong>
                </div>
              </MobileStats>
              <p>Your rebates · —</p>
            </MobileCard>
          );
        })}
      </MobileCards>
    </>
  );
}

export function PointsBudgetsView({
  data,
  api,
}: {
  data: IAffiliateDashboard;
  api: DashboardPresentationContext;
}) {
  return (
    <>
      <DashboardSection aria-label="Your points">
        <SectionHeading>
          <div>
            <h2>Your points</h2>
          </div>
          <Stats>
            <SmallStat>
              <small>Available</small>
              <strong>{pointAmount(data.balances.available_points)}</strong>
            </SmallStat>
            <SmallStat>
              <small>Pending</small>
              <strong>{pointAmount(data.balances.pending_points)}</strong>
            </SmallStat>
            <SmallStat>
              <small>Fee coverage</small>
              <strong>
                {money(
                  new Decimal(data.balances.available_points || "0")
                    .div(4)
                    .toString()
                )}
              </strong>
            </SmallStat>
          </Stats>
        </SectionHeading>
        <ExplanationGrid>
          <Explanation>
            <span>01</span>
            <h3>What points are</h3>
            <p>
              Fee credits you give to traders. 1 point covers $0.25 of trading
              fees. Points are separate from your USDC rebates and cannot be
              withdrawn as cash.
            </p>
          </Explanation>
          <Explanation>
            <span>02</span>
            <h3>How you get them</h3>
            <p>
              You earn points for referred trading above the floor. They vest
              for seven days, then stay available until 180 days after earning.
              A welcome grant, if you received one, has its own expiry.
            </p>
          </Explanation>
          <Explanation>
            <span>03</span>
            <h3>How you spend them</h3>
            <p>
              Allocate points as a zero-fee budget to a linked trader wallet.
              Four points cover $1 of that wallet’s trading fees.
            </p>
          </Explanation>
          <Explanation>
            <span>04</span>
            <h3>How budgets apply</h3>
            <p>
              {isFullFillWaiver(data.waiver_policy)
                ? "Each fill’s fee is covered in full or charged in full. If a budget cannot cover the whole fee, nothing is spent. Zero-fee fills earn no rewards or reward-eligible volume."
                : "Zero-fee budgets follow the current program fee policy. See your account’s fee details for the exact coverage rules."}
            </p>
          </Explanation>
        </ExplanationGrid>
        <WelcomeGrant data={data} api={api} />
        <PointsHistory data={data} api={api} />
      </DashboardSection>
      <DashboardSection aria-label="Zero-fee budgets">
        <SectionHeading>
          <div>
            <h2>Zero-fee budgets</h2>
          </div>
        </SectionHeading>
        <p>Fund is disabled in this read-only administrator view.</p>
        <SubsectionHeading>
          <h3>Wallets</h3>
          <small>Budgets for a single wallet</small>
        </SubsectionHeading>
        <WalletBudgetTable data={data} api={api} />
        <p>
          When a budget ends, unused value is forfeited. Recorded balances
          remain visible for review.
        </p>
      </DashboardSection>
    </>
  );
}
